import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Grid, Heading, ResponsiveContext, Text } from "grommet";
import * as React from "react";
import styled, { keyframes } from 'styled-components';

const pulseAnimation = keyframes`
  0% {
    opacity: 0.1;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 0.1;
  }
`;

const glowAnimation = keyframes`
  0% {
    box-shadow: 0 0 5px var(--accent-1);
  }
  50% {
    box-shadow: 0 0 20px var(--accent-1), 0 0 30px var(--accent-2);
  }
  100% {
    box-shadow: 0 0 5px var(--accent-1);
  }
`;
import { useLocation } from "react-router-dom";
import { usePermission } from "../account/AccountStore";
import CustomButton from "../components/CustomButton";
import SkeletonWrapper from "../components/SkeletonWrapper";
import StyledModal from "../components/StyledModel";
import CreateMatch from "./CreateMatch";
import { getAdHocMatches, MatchViewModel } from "./MatchesStore";
import { MatchRowWithActions } from "./MatchRowWithActions";

export const newMatchIcon = (
    <span className="fa-layers fa-fw">
        {/* @ts-ignore */}
        <FontAwesomeIcon size="lg" icon={["fad", "swords"]}></FontAwesomeIcon>
        <FontAwesomeIcon icon={["fad", "plus"]} swapOpacity transform="up-15 right-15" />
    </span>
);
/* @ts-ignore */
const archive = <FontAwesomeIcon size="xl" icon={["fad", "archive"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]}></FontAwesomeIcon>;

const MatchesIndex = () => {
    const [upcomingMatches, setUpcomingMatches] = React.useState([] as MatchViewModel[]);
    const [pastMatches, setPastMatches] = React.useState([] as MatchViewModel[]);
    const [loading, setLoading] = React.useState(true);

    let { pathname } = useLocation();

    const [showArchive, setShowArchive] = React.useState(false);

    const [upcomingSkip, setUpcomingSkip] = React.useState(0);
    const [pastSkip, setPastSkip] = React.useState(0);

    const [limit, setLimit] = React.useState(50);

    const [showMoreUpcoming, setShowMoreUpcoming] = React.useState(true);
    const [showMorePast, setShowMorePast] = React.useState(true);

    React.useEffect(() => {
        setLoading(true);
        loadMatches(true, true, 0).then((matches) => {
            setUpcomingMatches(matches.filter((match: MatchViewModel) => match.isUpcoming));
            setPastMatches(matches.filter((match: MatchViewModel) => !match.isUpcoming));
            if (matches.length < limit) {
                setShowMoreUpcoming(false);
                setShowMorePast(false);
            }
        });
    }, [pathname, showArchive]);

    const loadMatches = async (upcoming: boolean, past: boolean, skip: number) => {
        setLoading(true);
        let matches = await getAdHocMatches(upcoming, past, showArchive, skip, limit);
        setLoading(false);
        return matches;
    };

    const hasWritePermission = usePermission("sport:matches.write");

    const loadMoreUpcoming = async () => {
        setUpcomingSkip(upcomingSkip + limit);
        let moreMatches = await loadMatches(true, false, upcomingSkip + limit);
        if (moreMatches.length < limit) {
            setShowMoreUpcoming(false);
        }
        setUpcomingMatches([...upcomingMatches, ...moreMatches]);
    };

    const loadMorePast = async () => {
        setPastSkip(pastSkip + limit);
        let moreMatches = await loadMatches(false, true, pastSkip + limit);
        if (moreMatches.length < limit) {
            setShowMorePast(false);
        }
        setPastMatches([...pastMatches, ...moreMatches]);
    };

    const [showCreateMatchModal, setShowCreateMatchModal] = React.useState(false);

    const size = React.useContext(ResponsiveContext);

    return (
        <>
            <Box 
                background="linear-gradient(to right, #1a1a1a, #2a2a2a)" 
                pad="medium" 
                round="small"
                margin={{ bottom: "medium" }}
                animation={["fadeIn", "slideDown"]}
            >
                <Box 
                    direction="row" 
                    justify="between" 
                    align="center"
                >
                    <Box>
                        <Heading 
                            size="medium" 
                            margin="none" 
                            color="accent-1"
                            style={{ 
                                textTransform: "uppercase", 
                                textShadow: "0 0 10px var(--accent)",
                                letterSpacing: "2px"
                            }}
                        >
                            Match Arena
                        </Heading>
                        <Text size="small" color="accent-2">Epic Matches Await</Text>
                    </Box>
                    <Box direction="row" gap="small">
                        {hasWritePermission && (
                            <CustomButton 
                                onClick={() => setShowCreateMatchModal(true)} 
                                icon={newMatchIcon} 
                                label="New Match"
                                primary
                                color="accent-1"
                                style={{
                                    transform: "scale(1)",
                                    transition: "transform 0.2s ease",
                                    ":hover": {
                                        transform: "scale(1.05)"
                                    }
                                }}
                            />
                        )}
                        <CustomButton 
                            icon={archive} 
                            label={showArchive ? "Active Matches" : "Matches Archives"} 
                            onClick={() => setShowArchive(!showArchive)}
                            style={{
                                transform: "scale(1)",
                                transition: "transform 0.2s ease",
                                ":hover": {
                                    transform: "scale(1.05)"
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Box>
            <SkeletonWrapper loading={loading} style={{ 
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, var(--accent-1), var(--accent-2))',
                    opacity: 0.1,
                    animation: 'pulse 2s infinite'
                }
            }}>
                <Grid 
                    columns={size == "small" ? ["auto"] : ["1fr", "1fr"]} 
                    gap="medium"
                >
                    <Box
                        background="linear-gradient(to bottom, #2a2a2a, #1a1a1a)"
                        pad="medium"
                        round="medium"
                        gap="small"
                        border={{ color: 'accent-1', size: '2px' }}
                        style={{
                            boxShadow: '0 0 20px rgba(0,0,0,0.3)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <Box
                            direction="row"
                            align="center"
                            gap="small"
                            pad={{ bottom: "small" }}
                            border={{ side: 'bottom', color: 'accent-1', size: '1px' }}
                        >
                            <FontAwesomeIcon icon={["fad", "sword"]} size="lg" color="var(--accent)" />
                            <Text 
                                weight="bold" 
                                size="large"
                                color="accent-1"
                                style={{ 
                                    textTransform: "uppercase",
                                    letterSpacing: "1px"
                                }}
                            >
                                Upcoming Matches
                            </Text>
                        </Box>
                        <Box gap="small" animation={{ type: "fadeIn", duration: 500 }}>
                            {upcomingMatches.map((match, index) => (
                                <Box
                                    key={match.id}
                                    animation={{ type: "slideRight", delay: index * 100 }}
                                >
                                    <MatchRowWithActions match={match} onRevert={() => loadMatches(true, true, 0).then((matches) => {
                                        setUpcomingMatches(matches.filter((match: MatchViewModel) => match.isUpcoming));
                                        setPastMatches(matches.filter((match: MatchViewModel) => !match.isUpcoming));
                                    })} />
                                </Box>
                            ))}
                        </Box>
                        {showMoreUpcoming && (
                            <CustomButton
                                primary
                                color="accent-1"
                                label="Load More Matches"
                                onClick={loadMoreUpcoming}
                                style={{
                                    marginTop: '16px',
                                    transform: 'scale(1)',
                                    transition: 'transform 0.2s ease',
                                }}
                                hoverIndicator={{ color: 'accent-2', dark: true }}
                            />
                        )}
                    </Box>
                    <Box
                        background="linear-gradient(to bottom, #2a2a2a, #1a1a1a)"
                        pad="medium"
                        round="medium"
                        gap="small"
                        border={{ color: 'accent-2', size: '2px' }}
                        style={{
                            boxShadow: '0 0 20px rgba(0,0,0,0.3)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <Box
                            direction="row"
                            align="center"
                            gap="small"
                            pad={{ bottom: "small" }}
                            border={{ side: 'bottom', color: 'accent-2', size: '1px' }}
                        >
                            <FontAwesomeIcon icon={["fad", "trophy"]} size="lg" color="var(--accent-2)" />
                            <Text 
                                weight="bold"
                                size="large"
                                color="accent-2"
                                style={{ 
                                    textTransform: "uppercase",
                                    letterSpacing: "1px"
                                }}
                            >
                                Match History
                            </Text>
                        </Box>
                        <Box gap="small" animation={{ type: "fadeIn", duration: 500 }}>
                            {pastMatches.map((match, index) => (
                                <Box
                                    key={match.id}
                                    animation={{ type: "slideLeft", delay: index * 100 }}
                                >
                                    <MatchRowWithActions match={match} onRevert={() => loadMatches(true, true, 0).then((matches) => {
                                        setUpcomingMatches(matches.filter((match: MatchViewModel) => match.isUpcoming));
                                        setPastMatches(matches.filter((match: MatchViewModel) => !match.isUpcoming));
                                    })} />
                                </Box>
                            ))}
                        </Box>
                        {showMorePast && (
                            <CustomButton
                                primary
                                color="accent-2"
                                label="Load More History"
                                onClick={loadMorePast}
                                style={{
                                    marginTop: '16px',
                                    transform: 'scale(1)',
                                    transition: 'transform 0.2s ease',
                                }}
                                hoverIndicator={{ color: 'accent-1', dark: true }}
                            />
                        )}
                    </Box>
                </Grid>
            </SkeletonWrapper>
            <StyledModal isOpen={showCreateMatchModal} setIsOpen={setShowCreateMatchModal}>
                <CreateMatch></CreateMatch>
            </StyledModal>
        </>
    );
};

export default MatchesIndex;
